import { Sparkline } from "@/components/charts/Sparkline";
import { KpiTile } from "@/components/shared/kpi-tile";
import { COLUMN_HELP } from "@/lib/column-help";
import { hours as formatHours, money, percent } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { AccountRangeTotals } from "./accountTotals";

/**
 * The five headline figures for the selected range, at the top of Overview.
 *
 * The header carries profit and margin, but only those two, and only in the
 * app bar -- revenue, cost and hours were readable nowhere on this page without
 * summing a twelve-row table by hand. They are the first things asked of an
 * account and they should not require arithmetic.
 *
 * Every figure comes from `accountRangeTotals`, the same function behind the
 * header, so the tile and the bar above it cannot disagree.
 */
export function RangeTotals({
  totals,
  rangeLabel,
  benchmarkPct,
  trend,
}: {
  totals: AccountRangeTotals;
  rangeLabel: string;
  benchmarkPct: number | null;
  /** Margin by month, oldest first; drawn under the Margin tile. */
  trend?: (number | null)[];
}) {
  const scope = rangeLabel ? `Over ${rangeLabel.toLowerCase()}` : undefined;
  // No hours tile when the range has no hours to show (Aggregate users): a
  // tile reading "—" would only advertise a figure they may not see.
  return (
    <div
      className={cn(
        "grid gap-4 sm:grid-cols-2",
        totals.hours === null ? "xl:grid-cols-4" : "xl:grid-cols-5",
      )}
    >
      <KpiTile
        label="Revenue"
        value={money(totals.revenue)}
        hint={scope}
        help={COLUMN_HELP.revenue}
      />
      <KpiTile label="Cost" value={money(totals.cost)} hint={scope} help={COLUMN_HELP.cost} />
      <KpiTile
        label="Profit"
        value={money(totals.profit)}
        hint={scope}
        help={COLUMN_HELP.profit}
        tone={totals.profit < 0 ? "bad" : undefined}
      />
      <KpiTile
        label="Margin"
        // percent() renders a dash for null: no revenue has no margin, and 0%
        // would claim break-even, which is a different thing.
        value={percent(totals.marginPct)}
        hint={benchmarkPct !== null ? `Benchmark ${benchmarkPct}%` : "No benchmark set"}
        help={COLUMN_HELP.margin}
        tone={totals.marginPct !== null && totals.marginPct < 0 ? "bad" : undefined}
        spark={
          trend && trend.filter((v) => v !== null).length >= 2 ? (
            <Sparkline values={trend} label="Margin by month" />
          ) : undefined
        }
      />
      {totals.hours === null ? null : (
        <KpiTile
          label="Hours"
          value={formatHours(totals.hours)}
          hint={scope}
          help={COLUMN_HELP.hours}
        />
      )}
    </div>
  );
}
